import { EditorChangeType } from '../types';

import type { Track } from '../models/Track';
import type { VideoEditor } from '../VideoEditor';
import type { Command } from './Command';

export class CreateTrackCommand implements Command {
  private editor: VideoEditor;

  getName(): string {
    return 'CreateTrackCommand';
  }

  shouldCreateUndoRedoEntry(): boolean {
    return true;
  }

  private trackType: 'video' | 'audio' | 'text' | 'image';
  private _createdTrack?: Track;

  constructor(
    editor: VideoEditor,
    trackType: 'video' | 'audio' | 'text' | 'image' = 'video',
  ) {
    this.editor = editor;
    this.trackType = trackType;
  }

  /**
   * Get the created track (available after execution)
   */
  get createdTrack(): Track | undefined {
    return this._createdTrack;
  }

  execute(): boolean {
    const track = this.editor.timeline.createTrack(this.trackType);
    if (track) {
      this._createdTrack = track;
      this.editor.markDirty('create_track_command');
      this.editor.notifyObservers(EditorChangeType.TRACK_ADDED, {
        track: track,
      });
      return true;
    }
    return false;
  }

  undo(): boolean {
    if (!this._createdTrack) return false;
    const success = this.editor.timeline.removeTrack(this._createdTrack.id);
    if (success) {
      this.editor.markDirty('create_track_command_undo');
      this.editor.notifyObservers(EditorChangeType.TRACK_REMOVED, {
        trackId: this._createdTrack.id,
        track: this._createdTrack,
      });
    }
    return success;
  }

  redo(): boolean {
    if (!this._createdTrack) return false;
    // Re-add the track with the same ID
    const success = this.editor.timeline.addTrack(this._createdTrack);
    if (success) {
      this.editor.markDirty('create_track_command_redo');
      this.editor.notifyObservers(EditorChangeType.TRACK_ADDED, {
        track: this._createdTrack,
      });
    }
    return !!success;
  }

  getDescription(): string {
    return `Create ${this.trackType} track${
      this._createdTrack ? ` (${this._createdTrack.id})` : ''
    }`;
  }
}
